import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { AccountService } from 'src/app/services/account.service';
import { MapService } from 'src/app/services/map.service';
import { OtherService } from 'src/app/services/other.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-specialist-registration',
  templateUrl: './specialist-registration.component.html',
  styleUrls: ['./specialist-registration.component.css'],
})
export class SpecialistRegistrationComponent implements OnInit {
  showMap: boolean = (<any>window).forceAddress;
  forceAddress: number = (<any>window).forceAddress;
  addressPlaceHolder: string = '';
  isSubmitDisabled = false;
  showMapDialog = false;
  returnMobile: string | null = null;
  isLicenseMode = false;
  mobilePlaceholder = '* شماره موبایل';
  licenseNoPlaceholder = ' * شناسه شناور';

  formGroup = new FormGroup({
    strName: new FormControl('', [Validators.required]),
    strFamily: new FormControl('', [Validators.required]),
    strID: new FormControl('', [Validators.required, this.validateStrID()]),
    strMobile: new FormControl('', [
      Validators.required,
      this.validateStrMobile(),
    ]),
    strVIN: new FormControl(''),
    strLicenseNo: new FormControl('', Validators.required),
    iPower: new FormControl(null, [Validators.required]),
    strCity: new FormControl(null, [Validators.required]),
    tiFloatingType: new FormControl(null, [Validators.required]),
    strAddress: new FormControl(''),
    strComment: new FormControl(''),
    bHas12DC: new FormControl(true),
    bInstallment: new FormControl(false),
    strPaymentComment: new FormControl(''),
    iPayment1: new FormControl('0'),
  });

  mdl_Receipt: FormGroup = this._fb.group({
    iSystemUser: this.otherService.specialistUser.iSystemUser,
    strSession: this.otherService.specialistUser.strSession,
    iPreregistration: new FormControl('', Validators.required),
    iTrackingCode: new FormControl('', Validators.required),
    iPayment1: new FormControl('', Validators.required),
    bInstallment: new FormControl(false),
    strPaymentComment: new FormControl(''),
    strPaymentDate: new FormControl(''),
    strIMEI: new FormControl(''),
    strCellNo: new FormControl(''),
  });

  coordinates = {
    fLat: 0,
    fLon: 0,
  };

  floatingType$ = this.accountService.floatingType().pipe(
    map((response: string) => JSON.parse(response)),
    catchError(() => of(false)),
  );

  states$ = this.accountService.GetState().pipe(
    map((response: string) => JSON.parse(response)),
    catchError(() => of(false)),
  );

  cities$: Observable<any>;

  unSubscribeAll$ = new Subject<void>();

  @ViewChild('paymentReceiptDialog') paymentReceiptDialog!: TemplateRef<any>;

  constructor(
    private accountService: AccountService,
    private otherService: OtherService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private _fb: FormBuilder,
    private mapService: MapService,
    private ngbModal: NgbModal,
  ) {
    this.addressPlaceHolder = this.forceAddress
      ? '* آدرس محل نصب (الزامی)'
      : 'آدرس محل نصب (اختیاری)';
  }

  ngOnInit(): void {
    if (this.showMap) {
      this.formGroup.get('strAddress').addValidators(Validators.required);
    }

    this.formGroup.get('bInstallment')?.valueChanges.subscribe((value) => {
      if (value) {
        this.formGroup.get('iPayment1')?.setValue('100,000,000');
      } else {
        this.formGroup.get('iPayment1')?.setValue('0');
      }
    });

    const strMobile = this.activatedRoute.snapshot.queryParamMap.get('mobile');
    this.returnMobile = this.activatedRoute.snapshot.queryParamMap.get('returnMobile');
    const strMobileFormControl = this.formGroup.get('strMobile');
    strMobileFormControl.setValue(strMobile);
    strMobileFormControl.disable();

    const licenseNo =
      this.activatedRoute.snapshot.queryParamMap.get('licenseNo');

    // Entered with boat license: mobile becomes optional/editable, license becomes readonly.
    // Entered with mobile: current behavior is kept unchanged.
    this.isLicenseMode = !!licenseNo;

    if (this.isLicenseMode) {
      this.mobilePlaceholder = 'شماره موبایل (اختیاری)';
      this.licenseNoPlaceholder = '* شماره مجوز';

      const mobileCtrl = this.formGroup.get('strMobile');
      mobileCtrl?.clearValidators();
      mobileCtrl?.setValidators([this.validateStrMobileOptional()]);
      mobileCtrl?.updateValueAndValidity();

      const licenseCtrl = this.formGroup.get('strLicenseNo');
      licenseCtrl?.setValue(licenseNo);
      licenseCtrl?.disable();
    } else if (licenseNo && !this.formGroup.get('strLicenseNo')?.value) {
      this.formGroup.get('strLicenseNo')?.setValue(licenseNo);
    }
  }

  async submit() {
    if (
      this.formGroup.invalid ||
      this.formGroup.pristine ||
      this.isSubmitDisabled
    ) {
      return;
    }

    if (this.formGroup.value.bInstallment) {
      if (!this.formGroup.value.strPaymentComment) {
        this.markAllAsTouched(this.formGroup);
        return this.accountService.toastSwal(
          'error',
          'توضیحات شرایط پرداخت را وارد کنید!',
        );
      }

      if (
        !this.formGroup.value.iPayment1.trim() ||
        this.formGroup.value.iPayment1.trim() <= 0
      ) {
        this.markAllAsTouched(this.formGroup);
        return this.accountService.toastSwal(
          'error',
          'مبلغ پیش پرداخت را وارد کنید!',
        );
      }
    }

    const confirmDialog = await Swal.fire({
      title: 'آیا از ثبت نام اطمینان دارید؟',
      icon: 'warning',
      showCancelButton: true,
      showConfirmButton: true,
      reverseButtons: true,
      confirmButtonColor: '#10b981',
      confirmButtonText: 'تایید',
      cancelButtonText: 'لغو',
    });

    if (!confirmDialog.isConfirmed) {
      return;
    }

    this.isSubmitDisabled = true;

    const formValue = this.formGroup.getRawValue();

    const model = this.otherService.stringNumberToNumber({
      ...this.coordinates,
      ...formValue,
      iSystemUser: this.otherService.specialistUser.iSystemUser,
      strSession: this.otherService.specialistUser.strSession,
    });

    this.accountService
      .specialistRegistration(model)
      .pipe(
        catchError(() => of(false)),
        takeUntil(this.unSubscribeAll$),
      )
      .subscribe((response: string | false) => {
        this.isSubmitDisabled = false;

        if (response) {
          let result = JSON.parse(response);

          if (Array.isArray(result)) {
            result = result[0];
          }

          if (result?.ISSessionExpired == true) {
            this.otherService.logout();
            this.router.navigate(['/'], {
              queryParams: { error: 'SessionExpired' },
            });
            this.accountService.toastSwal(
              'error',
              'نشست کاربری شما منقضی شده است، مجددا وارد سامانه شوید',
            );
            return;
          }

          if (result.iResult === 0) {
            return this.accountService.toastSwal('error', result.strError);
          }

          this.accountService.toastSwal('success', `عملیات با موفقیت انجام شد`);

          this.openPaymentReceiptModal(result);
        } else {
          this.accountService.toastSwal('error', 'خطایی رخ داد');
        }
      });
  }

  openPaymentReceiptModal(result: any) {
    this.mdl_Receipt.patchValue({
      iPreregistration: result.iPreregistration,
      iTrackingCode: '',
      bInstallment: this.formGroup.get('bInstallment')?.value,
      strPaymentComment: this.formGroup.get('strPaymentComment')?.value,
      iPayment1: result.iPayment1.toLocaleString('en-US'),
    });

    const dialogRef = this.ngbModal.open(this.paymentReceiptDialog, {
      scrollable: true,

      container: 'app-specialist-registration',

      modalDialogClass: 'details-dialog',

      backdrop: 'static',

      keyboard: false,

      centered: true,

      animation: false,
    });

    dialogRef.dismissed.subscribe(() => {
      this.paymentReceiptDialog = null;

      this.navigateBackToVerification();
    });
  }

  navigateBackToVerification() {
    if (this.returnMobile) {
      this.router.navigate(['/specialist-verification'], {
        queryParams: { mobile: this.returnMobile },
      });
    } else {
      this.router.navigateByUrl('/specialist-verification');
    }
  }

  submitReceipt() {
    if (this.mdl_Receipt.invalid) {
      this.markAllAsTouched(this.mdl_Receipt);
      return this.accountService.toastSwal(
        'error',
        'مورارد الزامی را پر کنید!',
      );
    }

    if (
      this.mdl_Receipt.value.iTrackingCode > 0 &&
      !this.mdl_Receipt.value.iPayment1
    ) {
      this.markAllAsTouched(this.mdl_Receipt);
      return this.accountService.toastSwal(
        'error',
        'مبلغ واریزی را وارد کنید!',
      );
    }

    if (
      this.mdl_Receipt.value.bInstallment &&
      !this.mdl_Receipt.value.strPaymentComment
    ) {
      this.markAllAsTouched(this.mdl_Receipt);
      return this.accountService.toastSwal(
        'error',
        'توضیحات شرایط پرداخت را وارد کنید!',
      );
    }

    let model = this.otherService.stringNumberToNumber(this.mdl_Receipt.value);

    this.accountService
      .preregistration_SetCode(model)
      .pipe(
        catchError(() => of(false)),
        takeUntil(this.unSubscribeAll$),
      )
      .subscribe((response: string | false) => {
        if (response) {
          let result = JSON.parse(response);

          if (Array.isArray(result)) result = result[0];

          if (result?.ISSessionExpired == true) {
            this.otherService.logout();
            this.router.navigate(['/'], {
              queryParams: { error: 'SessionExpired' },
            });
            this.accountService.toastSwal(
              'error',
              'نشست کاربری شما منقضی شده است، مجددا وارد سامانه شوید',
            );
            return;
          }

          if (result.iResult === 0) {
            return this.accountService.toastSwal('error', result.strError);
          }

          this.accountService.toastSwal('success', result.strError);

          this.ngbModal.dismissAll();
          this.navigateBackToVerification();
        } else {
          this.accountService.toastSwal('error', 'خطایی رخ داد');
        }
      });
  }

  show_Map() {
    this.mapService
      .getCurrentLocation()
      .then((coords) => {
        this.coordinates.fLon = coords.longitude;
        this.coordinates.fLat = coords.latitude;
      })
      .finally(() => (this.showMapDialog = true));
  }

  submitMap(event: any) {
    this.formGroup.get('strAddress').setValue(event.strText);

    this.coordinates.fLon = event.longitude;
    this.coordinates.fLat = event.latitude;

    this.showMapDialog = false;
  }

  validateStrMobile(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value;
      const isValid = /^09/.test(controlValue ?? '');

      return isValid ? null : { mobilePhoneNumberWrong: true };
    };
  }

  validateStrMobileOptional(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = (control.value ?? '').toString().trim();
      if (!controlValue) {
        return null;
      }
      const isValid = /^09/.test(controlValue ?? '');

      return isValid ? null : { mobilePhoneNumberWrong: true };
    };
  }

  validateStrID(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const controlValue = control.value;
      const isValid = this.isValidIranianNationalCode(controlValue);
      return isValid ? null : { nationalCodeWrong: true };
    };
  }

  private isValidIranianNationalCode(code: string): boolean {
    if (!/^\d{10}$/.test(code ?? '')) {
      return false;
    }

    if (/^(\d)\1{9}$/.test(code)) {
      return false;
    }

    const digits = code.split('').map(Number);
    const check = digits[9];
    const sum = digits
      .slice(0, 9)
      .reduce((acc, digit, i) => acc + digit * (10 - i), 0);
    const remainder = sum % 11;

    return remainder < 2 ? check === remainder : check === 11 - remainder;
  }

  getCities(e: Event) {
    const stateId = (e.target as HTMLSelectElement).value;
    this.cities$ = this.accountService.GetCity(stateId).pipe(
      map((response: string) => JSON.parse(response)),
      catchError(() => of(false)),
    );
  }

  unSubscribeAll(): void {
    this.unSubscribeAll$.next();
    this.unSubscribeAll$.complete();
  }

  ngOnDestroy(): void {
    this.unSubscribeAll();
  }

  markAllAsTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.markAllAsTouched(control);
      }
    });
  }

  markAllAsUnTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsUntouched({ onlySelf: false });
      } else if (control instanceof FormGroup) {
        this.markAllAsUnTouched(control);
      }
    });
  }
}
